import React, { useCallback } from "react";
import styled from "styled-components/macro";
import {
  Divider as MuiDivider,
  Grid as MuiGrid,
  Typography,
  Paper as MuiPaper,
  Box,
  Button as MuiButton,
} from "@material-ui/core";
import { spacing } from "@material-ui/system";
import { Helmet } from "react-helmet-async";
import DragAndDropSelector from "./DragAndDropSelector";
import MultiChart from "./MultiChart";
import useClimateAndFlowsExplorer from "./useClimateAndFlowsExplorer";
import SelectControl from "../../../components/inputs/SelectControl";
import YearToggleSwitch from "./YearToggleSwitch";
import { REQUIRED_NUMBER_OF_GRAPH_SELECTIONS } from "./constants";
import { Alert } from "@material-ui/lab";
import Loader from "../../../components/Loader";
import { Link as RouterLink } from "react-router-dom";

const Divider = styled(MuiDivider)(spacing);
const Grid = styled(MuiGrid)(spacing);
const Paper = styled(MuiPaper)(spacing);
const Button = styled(MuiButton)(spacing);

const StyledAlert = styled(Alert)`
  & .MuiAlert-message {
    width: 100%;
  }
`;

const OptionsHeader = ({
  text,
  color = "secondary",
  variant = "subtitle1",
  ...rest
}) => (
  <Typography color={color} variant={variant} {...rest}>
    {text}
  </Typography>
);

const ClimateAndFlowsExplorer = () => {
  const { inputs, inputState, handleInputState, chartData } =
    useClimateAndFlowsExplorer();
  const handleSelectionChange = useCallback(
    (newSelections) => {
      handleInputState("graphs", newSelections);
    },
    [handleInputState]
  );
  return (
    <>
      <Helmet title="Climate & Flows Explorer" />
      <Typography variant="h3" gutterBottom display="inline">
        Climate and Flows Explorer
      </Typography>

      <Divider my={6} />

      <Box display="flex" flexDirection="column" style={{ gap: "16px" }}>
        <Paper p={4}>
          <Box display="flex" justifyContent="flex-end">
            <Button
              variant="contained"
              color="primary"
              component={RouterLink}
              to={`/tools/monitoring-locations-and-parameters-by-basin-region/${
                inputState?.regionalGroup || ""
              }`}
            >
              Review Monitoring Locations in this Region
            </Button>
          </Box>
          <Box pl="10px" pr="10px">
            <OptionsHeader text="Select a Basin Region" />
            <SelectControl
              label="Regions"
              id="regionalGroup"
              valueField="reg_group_ndx"
              displayField="reg_group_name"
              value={inputState.regionalGroup ?? ""}
              onChange={handleInputState}
              options={inputs.regionalGroups.data}
              isLoading={inputs.regionalGroups.isLoading}
            />
            {(() => {
              const flowGage = inputs.regionalFlowLocations?.data?.find(
                (loc) => loc.reg_group_ndx === inputState.regionalGroup
              );
              return flowGage ? (
                <Typography variant="caption" color="textSecondary">
                  <strong>Flow</strong> data is from{" "}
                  <strong>{flowGage.location_display_label}</strong>, the most
                  downstream gage in this region.
                </Typography>
              ) : null;
            })()}
          </Box>
        </Paper>

        {inputState.graphs.length === REQUIRED_NUMBER_OF_GRAPH_SELECTIONS && (
          <Paper p={4}>
            {chartData.length ? (
              <MultiChart data={chartData} />
            ) : (
              <StyledAlert icon={false} severity="info">
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Loader />
                  <Typography style={{ flex: 1, textAlign: "center" }}>
                    <strong>Loading!</strong> Building and Customizing Your
                    Graphs
                  </Typography>
                  <Loader />
                </Box>
              </StyledAlert>
            )}
          </Paper>
        )}

        <Paper p={4}>
          <Grid container>
            <Grid
              item
              xs={12}
              md={6}
              display="flex"
              style={{ flexDirection: "column" }}
              pl="10px"
              pr="10px"
            >
              <OptionsHeader text="Select a Year to Explore" />
              <SelectControl
                label="Years"
                id="year"
                valueField="explore_year"
                displayField="explore_year"
                value={inputState.year ?? ""}
                onChange={handleInputState}
                options={inputs.years.data}
                isLoading={inputs.years.isLoading}
              />
            </Grid>

            <Grid
              item
              xs={12}
              md={6}
              display="flex"
              style={{ flexDirection: "column" }}
              pl="10px"
              pr="10px"
            >
              <OptionsHeader text="Year Type Selector" />
              <YearToggleSwitch
                value={inputState.yearType}
                onChange={handleInputState}
              />
            </Grid>

            <Grid
              item
              xs={12}
              display="flex"
              style={{ flexDirection: "column" }}
              pl="10px"
              pr="10px"
            >
              <Grid item xs={12} mb="16px">
                <OptionsHeader text="Drag and Drop Two Graphs to Compare" />
              </Grid>
              <DragAndDropSelector
                options={inputs.graphOptions.data || []}
                maxSelections={REQUIRED_NUMBER_OF_GRAPH_SELECTIONS}
                onSelectionChange={handleSelectionChange}
                selections={inputState.graphs}
                idField="caf_group_ndx"
                titleField="caf_graph_title"
              />
            </Grid>
          </Grid>
        </Paper>
      </Box>
    </>
  );
};

export default ClimateAndFlowsExplorer;
